<template>
  <!-- All release notes -->
  <transition name="fade">
    <div v-if="displayed" class="release-notes dark-overlay v-centerer" @click="$emit('close', $event)">
      <div class="dark-overlay-content xlarge" @click.stop="">
        <CloseButton @click.native="$emit('close', $event)" />
        <h2 class="basic-sub-title release-notes__title v-centerer">
          {{ $t('release.ALL_RELEASE_NOTES') }}
          <Icon>{{ giftBold }}</Icon>
        </h2>
        <div class="notes-wrapper">
          <ExpandableSettingsItem
            v-for="(release, rIdx) in displayedReleases"
            ref="expandables"
            :key="release.version"
            :title="release.version"
            class="notes-wrapper__item"
            default-expand
            forget-state
          >
            <div slot="right" class="notes-wrapper__item__date">
              {{ release.displayDate }}
            </div>
            <div>
              <!-- NOTE: Re-call '$t' for specific i18n features, such as '@:...' or 'sth.uppercase', etc -->
              <!-- eslint-disable vue/no-v-html -->
              <div v-if="release.essential && release.essential.length" class="esssential-features">
                <h3>
                  {{ $t('release.ESSENTIAL_FEATURES') }}
                </h3>
                <div v-for="(feature, fIdx) in release.essential" :key="fIdx">
                  <div class="feature-title">
                    <Icon>{{ dot }}</Icon>
                    <span v-html="$t(`release.versions[${latestReleaseIndex + rIdx}].essential[${fIdx}].title`)" />
                  </div>
                  <div
                    v-if="feature.description"
                    class="feature-description"
                    v-html="$t(`release.versions[${latestReleaseIndex + rIdx}].essential[${fIdx}].description`)"
                  />
                </div>
              </div>
              <div v-if="release.common && release.common.length" class="notes-wrapper__item__standard">
                <h3>
                  {{ $t('release.STANDARD_FEATURES') }}
                </h3>
                <div v-for="(feature, fIdx) in release.common" :key="fIdx">
                  <div class="feature-title">
                    <Icon>{{ dot }}</Icon>
                    <span v-html="$t(`release.versions[${latestReleaseIndex + rIdx}].common[${fIdx}].title`)" />
                  </div>
                  <div
                    v-if="feature.description"
                    class="feature-description"
                    v-html="$t(`release.versions[${latestReleaseIndex + rIdx}].common[${fIdx}].description`)"
                  />
                </div>
                <div v-if="release.version === '2.0'" class="h-v-centerer">
                  <div
                    class="new-design-modal__video-wrapper"
                    :class="{ 'new-design-modal__video-wrapper--width600': !$store.state.device.isSmartphoneLandscape }"
                  >
                    <div class="new-design-modal__video-wrapper__video">
                      <iframe
                        src="https://www.youtube.com/embed/pY7z3_z4wiw"
                        title="Shizen new design"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="release.bugs && release.bugs.length" class="notes-wrapper__item__bugs">
                <h3>
                  {{ $t('release.BUG_FIXES') }}
                </h3>
                <div v-for="(feature, fIdx) in release.bugs" :key="fIdx">
                  <div class="feature-title">
                    <Icon>{{ dot }}</Icon>
                    <span v-html="$t(`release.versions[${latestReleaseIndex + rIdx}].bugs[${fIdx}].title`)" />
                  </div>
                  <div
                    v-if="feature.description"
                    class="feature-description"
                    v-html="$t(`release.versions[${latestReleaseIndex + rIdx}].bugs[${fIdx}].description`)"
                  />
                </div>
                <div class="feature-title">
                  <Icon>{{ dot }}</Icon>
                  <span v-html="$t('release.MINOR_BUG_FIXES')" />
                </div>
              </div>
              <!-- eslint-enable vue/no-v-html -->
            </div>
          </ExpandableSettingsItem>
          <button v-if="canSeeMore" class="option-button see-more" @click="seeMore">
            {{ $t('message.SEE_MORE') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Component to inform the user about the latest changes made per version
 */

import toastr from 'toastr';
import $ from 'jquery';
// Components
import CloseButton from '@/components/close-button.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import Icon from '@/components/icon.vue';
// Utils
import { dot, giftBold } from '@/assets/icons';

export default {

  name: 'ReleaseNotes',

  components: {
    CloseButton,
    ExpandableSettingsItem,
    Icon,
  },

  props: {
    displayed: Boolean,
  },

  data() {
    return {
      dot,
      giftBold,
      displayedNumber: 20,
    };
  },

  computed: {
    /** Array: Array of version displayed in the list */
    releases() {
      return this.$t('release.versions');
    },
    /** Number: Index of the latest release (skipping unreleased features) */
    latestReleaseIndex() {
      for (let i = 0, len = this.releases.length; i < len; i++) {
        if (!this.releases[i].unreleased) {
          return i;
        }
      }
      return 0;
    },
    /** Array: Limited number of versions so that we don't have an infinite number of versions displayed at a time */
    displayedReleases() {
      const df = this.$t('fullDateFormat');
      return this.releases
        .filter(r => !r.unreleased)
        .map((release) => {
          const essential = this.filterFeatures(release.essential);
          const common = this.filterFeatures(release.common);
          const bugs = this.filterFeatures(release.bugs);
          return {
            ...release,
            essential,
            common,
            bugs,
            displayDate: this.$moment(release.date).format(df),
          };
        })
        .slice(0, this.displayedNumber);
    },
    /** Boolean: There are still more versions to show */
    canSeeMore() {
      return this.displayedNumber < this.releases.length;
    },
  },

  created() {
    $(window).on(this.resizeEventName, () => {
      if (this.displayed) {
        this.$refs.expandables.forEach((comp) => {
          comp.itemExpand(true);
        });
      }
    });
  },

  methods: {
    /** See additional release notes */
    seeMore() {
      if (!this.canSeeMore) {
        toastr.remove();
        toastr.info(this.$t('release.ALL_RELEASE_DISPLAYED'));
      } else {
        this.displayedNumber += 20;
      }
    },
    /**
     * Filter an array of features from various parameters such as : requireGW
     * @param  {Array} features  Array ot filter
     * @return {Array}           Filtered array
     */
    filterFeatures(features) {
      if (!(features instanceof Array)) {
        return [];
      }
      return features.filter(feature => !feature.requireGW || this.currentTeam.accessGW === 1);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.release-notes.dark-overlay {
  cursor: initial;

  .dark-overlay-content {
    text-align: left;
    padding: 0px;
    box-sizing: border-box;

    h2.basic-sub-title {
      margin: 1.1rem 2rem;
      line-height: 1.4rem;
      font-size: 1.2rem;
      position: relative;

      svg {
        color: $color-light-blue-text;
        width: 1.3rem;
        height: 1.3rem;
        margin-left: 3rem;
      }
    }

    .notes-wrapper {
      height: calc(100% - 3.68rem);
      padding: .7rem 1.2em;
      box-sizing: border-box;
      overflow: auto;
      background: $color-body-background;

      h3 {
        font-size: 1em;
        font-weight: 650;
        color: $color-blue-text;
        margin: 0.2em 0px;
      }

      .feature-title {
        display: flex;
        align-items: center;
        margin-top: 0.35em;

        svg {
          width: 0.4em;
          height: 0.4em;
          margin-right: 1em;
          color: $color-light-blue-text;
          flex: 0 0 auto;
        }
        span {
          white-space: normal;
          flex: 1;
          font-weight: 600;
        }
      }

      .feature-description {
        white-space: normal;
        font-size: 0.85em;
        font-style: italic;
        margin: 0.3em 0;

        ul {
          margin: 0.2em 1.5em;
        }
      }

      button.see-more {
        margin-top: 0.5em;
        margin-bottom: 5em;
      }

      &__item {
        &__date {
          min-height: 1.3rem;
          font-style: italic;
          margin-left: 2rem;
        }

        &__standard, &__bugs {
          margin-top: 1rem;
        }
      }
    }
  }
}
</style>
